import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Page } from '/imports/ui/Page';
import { PageBody } from '/imports/ui/PageBody';
import { PageHeading } from '/imports/ui/PageHeading';
import { Panel } from '/imports/ui/Panel';
import { Stat, StatRow } from '/imports/ui/Stat';
import { ChipGroup } from '/imports/ui/ChipGroup';
import { Badge } from '/imports/ui/Badge';
import { Input } from '/imports/ui/Input';
import { Icon } from '/imports/ui/Icon';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { useMethod, useUser } from '/imports/hooks';
import { getTradeLog, TradeSessionRow, TradeIntent, TradeLogSort, TradeLogArg } from '/imports/api/analytics';
import { TradeInspection } from '/imports/ui/TradeInspection';
import { KINDS, kindVocab, INTENT_DOT, DIR_LABEL, fmtInt, fmtTix, fmtPct, hhmm, daysAgo, dayGroupLabel } from '/imports/ui/tradeVocab';

const RANGES = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
  { id: 'all', label: 'All' },
];
const rangeFrom = (id: string): Date | undefined => {
  const now = new Date();
  if (id === 'today') return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = { '7d': 7, '30d': 30, '90d': 90 }[id];
  return days ? new Date(now.getTime() - days * 86400000) : undefined;
};

const SORTS: { id: TradeLogSort; label: string }[] = [
  { id: 'recent', label: 'Recent' }, { id: 'tix', label: 'Top tix' }, { id: 'binder', label: 'Biggest binder' }, { id: 'opens', label: 'Most active' },
];
const INTENTS: TradeIntent[] = ['seller', 'buyer', 'hybrid'];
const OPENERS = [{ id: 'all', label: 'All' }, { id: 'eager', label: 'We invited' }, { id: 'inbound', label: 'Inbound' }];
const PAGE = 60;

/**
 * Every customer trade, as a forensic feed with one shared detail view.
 *
 * URL state carries the drill-in (`?t=` a trade, `?partner=` one trader's
 * timeline) so a trade can be linked from any other screen and the browser's
 * back button walks out of it. Filters are local: they describe a look, not a
 * place.
 */
export const TradeInspectorPage = () => {
  const user = useUser();
  const [params, setParams] = useSearchParams();
  const tradeId = params.get('t');
  const partnerParam = params.get('partner');
  const partnerId = partnerParam ? Number(partnerParam) : undefined;
  const partnerName = params.get('name') || partnerParam || '';

  // Range and kind live in the URL too, so the Dashboard's funnel numbers can
  // link straight to "completed trades in the last 7 days".
  const rangeId = params.get('range') || '7d';
  const kind = params.get('kind') || '';
  const setParam = (key: string, value: string) => setParams((p) => {
    if (value) p.set(key, value);
    else p.delete(key);
    return p;
  });
  const setRangeId = (id: string) => setParam('range', id === '7d' ? '' : id);
  const setKind = (k: string) => setParam('kind', k);
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [intent, setIntent] = useState<TradeIntent | ''>('');
  const [opener, setOpener] = useState('all');
  const [sort, setSort] = useState<TradeLogSort>('recent');
  const [limit, setLimit] = useState(PAGE);

  useEffect(() => {
    const h = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(h);
  }, [q]);
  useEffect(() => setLimit(PAGE), [rangeId, kind, qDebounced, intent, opener, sort, partnerId]);

  const arg = useMemo((): TradeLogArg => ({
    from: rangeFrom(rangeId),
    kind: kind || undefined,
    q: qDebounced || undefined,
    intent: intent || undefined,
    sort,
    initiatedByUs: opener === 'eager' ? true : opener === 'inbound' ? false : undefined,
    partnerId: Number.isFinite(partnerId) ? partnerId : undefined,
    limit,
  }), [rangeId, kind, qDebounced, intent, opener, sort, partnerId, limit]);
  // `tradeId` is a dep as well as the guard: arriving on a deep link skips the
  // fetch, and stepping back must then run it rather than show an empty log.
  const [log, loading] = useMethod(getTradeLog, arg, [arg, !tradeId], () => !tradeId);

  if (!user?.isSuperUser) return <NotPermittedPage />;

  const open = (id: string) => setParams((p) => { p.set('t', id); return p; });
  const back = () => setParams((p) => { p.delete('t'); return p; });
  const filterPartner = (id: number, name: string) => setParams((p) => { p.set('partner', String(id)); p.set('name', name); p.delete('t'); return p; });
  const clearPartner = () => setParams((p) => { p.delete('partner'); p.delete('name'); return p; });

  if (tradeId) {
    return (
      <Page>
        <PageHeading left={<h1>Trade Inspector</h1>} />
        <PageBody>
          <TradeInspection id={tradeId} onBack={back} onOpen={open} onFilterPartner={filterPartner} />
        </PageBody>
      </Page>
    );
  }

  const kindCount = (id: string) => log?.byKind.find((k) => k.kind === id)?.count ?? 0;
  const completed = kindCount('completed');
  const rows = log?.rows ?? [];

  return (
    <Page>
      <PageHeading
        left={<h1>Trade Inspector</h1>}
        right={<ChipGroup ariaLabel="Date range" chips={RANGES} value={rangeId} onChange={(id) => setRangeId(id)} />}
      />
      <PageBody>
        {loading && !log ? <LoadingFlex /> : !log ? null : (
          <div className="flex flex-col gap-3 pb-6">
            {log.collectionSize === 0 ? (
              <Panel>
                <div className="flex flex-col items-center gap-2 py-16 text-center">
                  <div className="text-[15px] font-semibold text-fg-1">No trades recorded yet</div>
                  <div className="max-w-[440px] text-[13px] text-fg-3">
                    Trade sessions are captured from the moment a bot accepts a customer trade or a customer accepts ours.
                    Internal transfers between our own bots are deliberately excluded.
                  </div>
                </div>
              </Panel>
            ) : (
              <>
                <StatRow>
                  <Stat label="Trades" value={fmtInt(log.matched)} icon="coPresent" hint={partnerId ? `with ${partnerName}` : `in ${RANGES.find((r) => r.id === rangeId)?.label ?? rangeId}`} />
                  <Stat
                    label="Completed"
                    value={log.total ? fmtPct((completed / log.total) * 100) : '--'}
                    icon="circleCheck"
                    hint={`${fmtInt(completed)} of ${fmtInt(log.total)}`}
                  />
                  <Stat label="Unique traders" value={fmtInt(log.uniqueTraders)} icon="group" hint="counterparties" />
                  <Stat label="Tix moved" value={fmtTix(log.tixMoved)} icon="coins" hint="bought + sold" />
                  <Stat label="Ticket shortfall" value={fmtInt(log.shortfallCount)} icon="warning" hint="accepted while short of tickets" />
                </StatRow>

                {/* The outcome mix doubles as the kind filter, so reading and drilling are the same gesture. */}
                <Panel eyebrow="Outcomes" title="Where trades end" actions={
                  kind ? (
                    <button type="button" onClick={() => setKind('')} className="cursor-pointer rounded border-none bg-transparent px-1 text-[12px] text-fg-3 hover:text-fg-1">
                      clear filter
                    </button>
                  ) : undefined
                }>
                  <div className="flex flex-col gap-2">
                    <div className="flex h-3.5 w-full gap-px overflow-hidden rounded-sm bg-page-alt">
                      {KINDS.map((k) => {
                        const n = kindCount(k.id);
                        if (!n || !log.total) return null;
                        return (
                          <div
                            key={k.id}
                            title={`${k.label}: ${fmtInt(n)} — ${k.blurb}`}
                            onClick={() => setKind(kind === k.id ? '' : k.id)}
                            className={`${k.bar} cursor-pointer transition-opacity${kind && kind !== k.id ? ' opacity-25' : ''}`}
                            style={{ width: `${(n / log.total) * 100}%` }}
                          />
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                      {KINDS.map((k) => (
                        <button
                          key={k.id}
                          type="button"
                          title={k.blurb}
                          aria-pressed={kind === k.id}
                          onClick={() => setKind(kind === k.id ? '' : k.id)}
                          className={`flex cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 text-[12px] transition-opacity${kind && kind !== k.id ? ' opacity-45' : ''}`}
                        >
                          <span className={`h-2.5 w-2.5 rounded-[3px] ${k.bar}`} />
                          <span className="text-fg-2">{k.label}</span>
                          <span className="num font-bold text-fg-1">{fmtInt(kindCount(k.id))}</span>
                          <span className="num text-[11px] text-fg-3">{log.total ? fmtPct((kindCount(k.id) / log.total) * 100) : ''}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </Panel>

                {/* controls */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="min-w-[200px] max-w-xs flex-1">
                    <Input value={q} onChange={(v) => setQ(v ?? '')} placeholder="Search counterparty or bot" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    {INTENTS.map((it) => (
                      <button
                        key={it}
                        type="button"
                        aria-pressed={intent === it}
                        onClick={() => setIntent(intent === it ? '' : it)}
                        className={`inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border px-2.5 text-[12px] capitalize transition-colors ${intent === it ? 'border-mid bg-page-alt text-fg-1' : 'border-soft bg-transparent text-fg-3 hover:bg-page-alt/60 hover:text-fg-1'}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${INTENT_DOT[it]}`} />
                        {it}
                      </button>
                    ))}
                  </div>
                  <ChipGroup ariaLabel="Who opened" chips={OPENERS} value={opener} onChange={(id) => setOpener(id)} />
                  <div className="ml-auto flex items-center gap-2">
                    {partnerId !== undefined && (
                      <button type="button" onClick={clearPartner} className="inline-flex h-8 cursor-pointer items-center gap-2 rounded-md border border-mid bg-page-alt pl-2.5 pr-2 text-[12px] text-fg-1 hover:bg-page">
                        <span className="text-fg-3">Trader</span>
                        <span className="num font-semibold">{partnerName}</span>
                        <Icon icon="close" size={13} />
                      </button>
                    )}
                    <ChipGroup ariaLabel="Sort" chips={SORTS} value={sort} onChange={(id) => setSort(id as TradeLogSort)} />
                  </div>
                </div>

                {rows.length === 0 ? (
                  <Panel><div className="py-12 text-center text-[13px] text-fg-3">No trades match.</div></Panel>
                ) : (
                  <>
                    <TradeFeed rows={rows} grouped={sort === 'recent'} onOpen={open} />
                    {log.matched > rows.length && (
                      <button type="button" onClick={() => setLimit((l) => Math.min(l + PAGE, 500))} disabled={limit >= 500} className="w-full cursor-pointer rounded-lg border border-dashed border-soft bg-transparent py-1.5 text-[12px] text-fg-3 transition-colors hover:border-mid hover:text-fg-1 disabled:cursor-default">
                        {limit >= 500 ? `Showing the newest ${fmtInt(rows.length)} of ${fmtInt(log.matched)}; narrow the range to see the rest` : `Show ${fmtInt(Math.min(PAGE, log.matched - rows.length))} more of ${fmtInt(log.matched - rows.length)}`}
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </PageBody>
    </Page>
  );
};

/** The feed: trades grouped by day in Recent sort, a flat ranked list otherwise. */
const TradeFeed = ({ rows, grouped, onOpen }: { rows: TradeSessionRow[]; grouped: boolean; onOpen: (id: string) => void }) => {
  if (!grouped) return <div className="grid gap-1.5">{rows.map((r) => <FeedRow key={r._id} r={r} onOpen={onOpen} />)}</div>;
  const groups: { key: number; rows: TradeSessionRow[] }[] = [];
  for (const r of rows) {
    const d = daysAgo(r.at);
    const last = groups[groups.length - 1];
    if (last && last.key === d) last.rows.push(r);
    else groups.push({ key: d, rows: [r] });
  }
  return (
    <div className="grid gap-5">
      {groups.map((g) => {
        const moved = g.rows.reduce((a, r) => a + r.tixIn + r.tixOut, 0);
        return (
          <div key={g.key}>
            <div className="mb-2 flex items-baseline gap-2 px-0.5">
              <h3 className="m-0 text-[13px] font-bold text-fg-1">{dayGroupLabel(g.key)}</h3>
              <span className="num text-[11px] text-fg-3">{fmtInt(g.rows.length)} trades · {fmtTix(moved)} moved</span>
            </div>
            <div className="grid gap-1.5">{g.rows.map((r) => <FeedRow key={r._id} r={r} onOpen={onOpen} />)}</div>
          </div>
        );
      })}
    </div>
  );
};

const Metric = ({ value, label, tone }: { value: string; label: string; tone: 'neutral' | 'green' | 'blue' }) => {
  const cls = { neutral: 'text-fg-2', green: 'text-primary', blue: 'text-info' }[tone];
  return (
    <span className="inline-flex flex-col items-center px-1.5 leading-none">
      <span className={`num text-[12px] font-bold ${cls}`}>{value}</span>
      <span className="eyebrow mt-0.5 text-[9px] text-fg-3">{label}</span>
    </span>
  );
};

const FeedRow = ({ r, onOpen }: { r: TradeSessionRow; onOpen: (id: string) => void }) => {
  const k = kindVocab(r.kind);
  const moved = r.tixIn + r.tixOut;
  return (
    <button
      type="button"
      onClick={() => onOpen(r._id)}
      className="group flex w-full cursor-pointer items-stretch overflow-hidden rounded-lg border border-soft bg-panel p-0 text-left transition-colors hover:border-mid hover:bg-page-alt/40"
    >
      <span className={`w-1 shrink-0 ${k.bar}`} />
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5">
        <div className="num w-12 shrink-0 text-[12px] text-fg-3">{hhmm(r.at)}</div>
        <div className="w-[200px] shrink-0">
          <div className="flex items-center gap-1.5">
            <span className="num truncate text-[13px] text-fg-1">{r.partner}</span>
            {r.partnerIsBot != null && r.partnerIsBot >= 0.5 && <Badge tone="blue" title={`bot confidence ${Math.round(r.partnerIsBot * 100)}%`}>bot</Badge>}
          </div>
          <div className="text-[11px] capitalize text-fg-3">{r.intent} · {fmtInt(r.partnerOpens)} opens</div>
        </div>
        <div className="flex shrink-0 items-center">
          <Metric value={fmtInt(r.binderCount)} label="binder" tone="neutral" />
          <span className="h-6 w-px bg-soft" />
          <Metric value={r.wantedCount ? fmtInt(r.wantedCount) : '–'} label="wanted" tone={r.wantedCount ? 'green' : 'neutral'} />
          <span className="h-6 w-px bg-soft" />
          <Metric value={r.takenCount ? fmtInt(r.takenCount) : '–'} label="took" tone={r.takenCount ? 'blue' : 'neutral'} />
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Badge tone={k.tone}>{k.label}</Badge>
          {r.creditPayout ? <span className="text-[12px] text-fg-3">Credit payout</span>
            : r.direction !== 'none' && <span className="hidden truncate text-[12px] text-fg-3 lg:inline">{DIR_LABEL[r.direction]}</span>}
          {r.initiatedByUs && <Badge tone="gold">we invited</Badge>}
          {r.ticketShortfall && <Badge tone={r.shortfallResolved ? 'amber' : 'red'}>{r.shortfallResolved ? 'was short' : 'short of tix'}</Badge>}
        </div>
        <div className="ml-auto shrink-0 text-right">
          <div className="num text-[13px] font-bold text-fg-1">{moved ? fmtTix(moved) : <span className="text-fg-3">·</span>}</div>
          <div className="num text-[10px] text-fg-3">{r.bot}</div>
        </div>
        <Icon icon="chevronRight" size={15} className="shrink-0 text-fg-3 opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
    </button>
  );
};
